import { CommonModule } from '@angular/common'
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { ChannelTrackData, ChannelTrackDataFlags } from '../../../interfaces/channel-track-data'
import { LocalChannel } from '../../../interfaces/local-channel'
import { ChannelService } from '../../../services/channel.service'
import { HTVButtonControllerDirective } from '../../../app-button-controller.directive'
import { LanguageService } from '../../../services/language.service'
import { PlatformService } from '../../../platforms/platform.service'
import { HTVPlatform } from '../../../platforms/platform'

interface ExtendedChannelTrackData {
  data: ChannelTrackData
  nativeLanguage: string
}

/**
 * Original audio and subtitle menu with a button that trigger the display of a dialog
 */
@Component({
  selector: 'app-tracks-menu-v1',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './tracks-menu-v1.component.html',
  styleUrls: ['./tracks-menu-v1.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class TracksMenuV1Component implements OnDestroy, OnChanges {
  channelTrackSubscription?: Subscription

  @Input() channel!: LocalChannel

  hidden = true

  isSubtitleEnabled = false
  currentAudioTrack?: ExtendedChannelTrackData
  currentSubtitleTrack?: ExtendedChannelTrackData
  audioTracks: ChannelTrackData[] = []
  subtitleTracks: ChannelTrackData[] = []
  extendedAudioTracks: ExtendedChannelTrackData[] = []
  extendedSubtitleTracks: ExtendedChannelTrackData[] = []

  readonly ChannelTrackDataFlags = ChannelTrackDataFlags

  platformType!: HTVPlatform
  HTVPlatform = HTVPlatform

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private channelService: ChannelService,
    private languageService: LanguageService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  ngOnDestroy(): void {
    this.channelTrackSubscription?.unsubscribe()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.platformType = this.platformService.type
    this.reset()
    this.registerChannelTrackListener()
  }

  reset(): void {
    this.hidden = true
    this.channelTrackSubscription?.unsubscribe()
    this.isSubtitleEnabled = false
    this.currentAudioTrack = undefined
    this.currentSubtitleTrack = undefined
    this.audioTracks = []
    this.subtitleTracks = []
    this.extendedAudioTracks = []
    this.extendedSubtitleTracks = []
  }

  registerChannelTrackListener(): void {
    this.channelTrackSubscription = this.channel.channelData$.subscribe(channelData => {
      if (channelData) {
        let hasListChanges = false
        this.isSubtitleEnabled = channelData.isSubtitleEnabled

        if (JSON.stringify(this.currentAudioTrack?.data) != JSON.stringify(channelData.currentAudioTrack)) {
          if (channelData.currentAudioTrack) {
            this.currentAudioTrack = this.mapToExtendedChannelTrackData(channelData.currentAudioTrack)
          } else {
            this.currentAudioTrack = undefined
          }
        }

        if (JSON.stringify(this.currentSubtitleTrack) != JSON.stringify(channelData.currentSubtitleTrack)) {
          if (channelData.currentSubtitleTrack) {
            this.currentSubtitleTrack = this.mapToExtendedChannelTrackData(channelData.currentSubtitleTrack)
          } else {
            this.currentSubtitleTrack = undefined
          }
        }

        if (JSON.stringify(this.audioTracks) != JSON.stringify(channelData.audioTracks)) {
          hasListChanges = true
          this.audioTracks = channelData.audioTracks
          this.extendedAudioTracks = channelData.audioTracks.map(at => this.mapToExtendedChannelTrackData(at))
        }

        if (JSON.stringify(this.subtitleTracks) != JSON.stringify(channelData.subtitleTracks)) {
          hasListChanges = true
          this.subtitleTracks = channelData.subtitleTracks
          this.extendedSubtitleTracks = channelData.subtitleTracks.map(st => this.mapToExtendedChannelTrackData(st))
        }

        // Reload button directive because lists have changed
        if (hasListChanges && !this.hidden) {
          this.appButtonDirective.initSelectedButton()
        }
      } else {
        // do nothing
      }
    })
  }

  toggleHidden(): void {
    this.hidden = !this.hidden
  }

  hide(): void {
    this.hidden = true
  }

  setAudioTrack(track: ChannelTrackData): void {
    this.channelService.setAudioTrack(track)
  }

  setSubtitleTrack(track: ChannelTrackData): void {
    if (this.currentSubtitleTrack?.data.index == track.index) {
      if (this.isSubtitleEnabled) {
        this.channelService.setSubtitleTrack(track, false)
      } else {
        this.channelService.setSubtitleTrack(track, true)
      }
    } else {
      this.channelService.setSubtitleTrack(track, true)
    }
  }

  mapToExtendedChannelTrackData(data: ChannelTrackData): ExtendedChannelTrackData {
    const nativeLanguage = this.languageService.getNativeLanguage(data.lang)
    return {
      data: data,
      nativeLanguage: nativeLanguage,
    }
  }
}
